/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { format, parse, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  getGetApiV5FirmsRequestsIdQueryKey,
  SignableDocumentSchema,
  useGetApiV5FirmsRequestsId,
  usePostApiV5FirmsWorkflowRequestsBulkDispatch,
  usePutApiV5FirmsRequestsId,
} from '@liscio/api';
import {
  useRequestStore,
  SignedUrlPreview,
  useRequestNavigation,
  useSignedUrl,
  WorkflowSignableDocument,
  WorkflowSignablePreviewDocument,
} from '@liscio/common';
import {
  Stack,
  InputLabel,
  Link,
  Icon,
  Button,
  Tooltip,
  Skeleton,
  Typography,
} from '@liscio/ui';
import { parseValidationErrors } from '@liscio/utils';

import { SelectOwnerDropdown } from '../../../../components/SelectOwnerDropdown/SelectOwnerDropdown';
import { AdvancedOptions } from '../AdvancedOptions/AdvancedOptions';
import { ClientPreviewDialog } from '../ClientPreviewDialog/ClientPreviewDialog';
import type {
  ClientPreviewTourCandidate,
  ClientPreviewTourNavigateSignal,
} from '../ClientPreviewDialog/clientPreviewTourCandidates';
import type {
  GuidedCustomStepCommand,
  GuidedCustomStepCommandSignal,
} from '../ClientPreviewDialog/customStepGuidedDemo';
import { PaymentReminderSettings } from '../PaymentReminderSettings/PaymentReminderSettings';
import { RequestRecipientSelector } from '../RequestRecipientSelector';
import { RHFDateInput } from 'components';
import { useGetAllItemsInSections } from 'modules/requests/hooks/useGetAllItemsInSections';
import { useReorderPreviewItems } from 'modules/requests/hooks/useReorderPreviewItems';
import { useRequestFilterStore } from 'modules/requests/hooks/useRequestFilterStore';
import { findSignableItem } from 'modules/requests/utils';
import { getSignedDocumentsFromItems } from 'modules/requests/utils/getSignedDocumentsFromItems';
import { routes } from 'routes';

type RequestDispatchProps = {
  open: boolean;
  requestId: string;
  width?: string;
  maxWidth?: string;
  previewOpen?: boolean;
  onPreviewOpenChange?: (open: boolean) => void;
  openCustomStepDialogSignal?: number;
  onGuidedTourClose?: () => void;
  onClientPreviewTourCandidatesChange?: (
    candidates: ClientPreviewTourCandidate[]
  ) => void;
  clientPreviewTourNavigateSignal?: ClientPreviewTourNavigateSignal | null;
  onGuidedCustomStepSaved?: () => void;
  guidedCustomStepCommandSignal?: GuidedCustomStepCommandSignal | null;
  onGuidedDemoCommandComplete?: (command: GuidedCustomStepCommand) => void;
};

type FormDataType = {
  title?: string;
  owner?: string;
  dueDate?: string;
  assignedAccountId?: string;
  assignedContactIds?: string[];
};

const DATE_FORMAT = 'MMM d, yyyy';

/**
 * This component is used to edit a request in a drawer.
 */
export function RequestDispatch({
  open,
  requestId,
  width,
  maxWidth,
  previewOpen,
  onPreviewOpenChange,
  openCustomStepDialogSignal,
  onGuidedTourClose,
  onClientPreviewTourCandidatesChange,
  clientPreviewTourNavigateSignal,
  onGuidedCustomStepSaved,
  guidedCustomStepCommandSignal,
  onGuidedDemoCommandComplete,
}: RequestDispatchProps) {
  const { data: requestData } = useGetApiV5FirmsRequestsId(requestId || '', {
    query: { enabled: open },
  });
  const { mutateAsync: updateRequest } = usePutApiV5FirmsRequestsId();
  const { mutateAsync: dispatchRequest } =
    usePostApiV5FirmsWorkflowRequestsBulkDispatch();
  const { getRefetchRequests } = useRequestFilterStore();
  const [pdfToken, setPdfToken] = useState<string | null>(null);
  const {
    signedUrlData,
    setRetrievalToken,
    refreshSignedUrl,
    clearSignedUrl,
    isLoading: isSignedUrlLoading,
  } = useSignedUrl();

  const [initialLoad, setInitialLoad] = useState(true);
  const [internalPreviewOpen, setInternalPreviewOpen] = useState(false);
  const updating = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { validationErrors, goBackUrl } = useRequestStore();
  const hasValidationErrorsInStore = validationErrors.length > 0;
  const parsedValidationErrors = useMemo(
    () => parseValidationErrors(validationErrors),
    [validationErrors]
  );
  const { isTaxDeliveryRequest } = useRequestNavigation();

  const isPreviewOpen = previewOpen ?? internalPreviewOpen;
  const setPreviewOpen = (nextOpen: boolean) => {
    if (onPreviewOpenChange) {
      onPreviewOpenChange(nextOpen);
      return;
    }

    setInternalPreviewOpen(nextOpen);
  };

  const handleClientPreviewClose = () => {
    setPreviewOpen(false);
    onGuidedTourClose?.();
  };

  useReorderPreviewItems(
    requestId || '',
    Boolean(requestId) && isTaxDeliveryRequest && open
  );

  const { items, isLoading: requestItemsLoading } =
    useGetAllItemsInSections(isTaxDeliveryRequest);
  const signableDocuments = useMemo(
    () => getSignedDocumentsFromItems(items),
    [items]
  );
  const signableItem = useMemo(() => findSignableItem(items), [items]);
  const signableObject = signableItem?.dependency?.object as
    | SignableDocumentSchema
    | undefined;

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    getValues,
    formState: { isValid, isDirty },
  } = useForm<FormDataType>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      owner: '',
      dueDate: '',
      assignedAccountId: '',
      assignedContactIds: [],
    },
  });

  useEffect(() => {
    if (requestData) {
      let dueDate = '';
      try {
        dueDate = requestData.due_date
          ? format(parseISO(requestData.due_date), 'yyyy-MM-dd')
          : '';
      } catch (error) {
        console.error('Error parsing date:', error);
      }

      reset({
        title: requestData.title || '',
        owner: requestData.owner?.uuid || '',
        dueDate,
        assignedAccountId: requestData.assigned_account?.uuid || '',
        assignedContactIds: requestData.assigned_contacts?.map(
          (contact) => contact.uuid
        ),
      });
      setInitialLoad(false);
    }
  }, [requestData, reset]);

  useEffect(() => {
    if (!initialLoad) {
      // this is necessary so that validation happens on initial load
      // so we can disable the submit if necessary
      trigger();
    }
  }, [initialLoad]);

  useEffect(() => {
    if (isDirty && !updating.current) {
      updating.current = true;
      const formValues = getValues();
      onFormChange(formValues);
    }
  }, [isDirty, getValues]);

  const onFormChange = (formData: FormDataType) => {
    let isoDate = '';
    try {
      isoDate = formData.dueDate
        ? format(
            parse(formData.dueDate, 'yyyy-MM-dd', new Date()),
            "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
          )
        : '';
    } catch (error) {
      console.error('Error parsing date:', error);
    }

    // Handle form "submission"
    updateRequest(
      {
        id: requestId || '',
        data: {
          title: formData.title,
          owner_id: formData.owner,
          due_date: isoDate,
          assigned_contact_ids: formData.assignedContactIds,
          assigned_account_id: formData.assignedAccountId,
        },
      },
      {
        onSuccess: (response) => {
          const refetchRequests = getRefetchRequests();
          queryClient.invalidateQueries(
            getGetApiV5FirmsRequestsIdQueryKey(requestId || '')
          );
          if (refetchRequests) {
            refetchRequests();
          }

          reset(
            {
              title: response.title || '',
              owner: response.owner?.uuid || '',
              dueDate: response.due_date
                ? format(parseISO(response.due_date), 'yyyy-MM-dd')
                : '',
              assignedAccountId: response.assigned_account?.uuid || '',
              assignedContactIds: response.assigned_contacts?.map(
                (contact) => contact.uuid
              ),
            },
            {
              keepIsValid: true,
              keepErrors: true,
            }
          );
          updating.current = false;
          // navigate(routes.requestsListProtected.link());
        },
        onError: (error) => {
          toast.error('Error updating request.');
          console.error('Error updating request:', error);
          updating.current = false;
        },
      }
    );
  };

  const handleDispatch = () => {
    dispatchRequest(
      {
        data: {
          request: {
            ids: [requestId || ''],
          },
        },
      },
      {
        onSuccess: () => {
          const refetchRequests = getRefetchRequests();
          if (refetchRequests) {
            refetchRequests();
          }
          toast.success('Request dispatched successfully.');
          navigate(goBackUrl || routes.requestsListProtected.link());
        },
        onError: (error) => {
          toast.error('Error dispatching request.');
          console.error('Error dispatching request:', error);
        },
      }
    );
  };

  const handleSourceFileClick = (token: string | null) => {
    if (token) {
      setPdfToken(token);
      setRetrievalToken(token);
      refreshSignedUrl();
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(handleDispatch)}
        style={{ height: '100%', width, maxWidth }}
      >
        <Stack height="100%">
          <Stack flexGrow={1} gap={2} padding="1rem">
            {requestData?.source_documents &&
              requestData.source_documents.length > 0 && (
                <Stack>
                  <InputLabel>SOURCE FILE</InputLabel>
                  <Link
                    href={'#'}
                    onClick={() =>
                      handleSourceFileClick(
                        requestData.source_documents[0].retrieval_token
                      )
                    }
                    display="flex"
                    alignItems="center"
                    disabled={isSignedUrlLoading}
                  >
                    <Icon icon="filePdf" sx={{ marginRight: '5px' }} />
                    {requestData?.source_documents?.[0]?.filename}
                  </Link>
                </Stack>
              )}
            {(signableDocuments.length > 0 || requestItemsLoading) &&
              isTaxDeliveryRequest && (
                <Stack gap={1}>
                  <InputLabel>SIGNED DOCUMENT</InputLabel>
                  {requestItemsLoading && <Skeleton />}
                  {signableDocuments.map((document) => (
                    <Link
                      href={'#'}
                      onClick={() =>
                        handleSourceFileClick(document.retrieval_token)
                      }
                      display="flex"
                      alignItems="start"
                      sx={{ wordBreak: 'break-word' }}
                      disabled={isSignedUrlLoading}
                    >
                      <Icon icon="filePdf" sx={{ marginRight: '5px' }} />
                      {document.filename}
                    </Link>
                  ))}
                </Stack>
              )}
            <Stack>
              <InputLabel>OWNER</InputLabel>
              <SelectOwnerDropdown control={control} name="owner" />
            </Stack>
            <Stack gap={2}>
              <Stack
                data-tour-id={
                  isTaxDeliveryRequest ? 'tax-delivery-due-date' : undefined
                }
              >
                <InputLabel>DUE DATE</InputLabel>
                <RHFDateInput
                  name="dueDate"
                  control={control}
                  required={true}
                  format={DATE_FORMAT}
                  valueFormat="yyyy-MM-dd"
                  minDate={new Date()}
                  placeholder="Select due date"
                />
              </Stack>
              <Stack
                data-tour-id={
                  isTaxDeliveryRequest ? 'tax-delivery-assignees' : undefined
                }
              >
                <RequestRecipientSelector
                  name="recipients"
                  control={control}
                  account={requestData?.assigned_account}
                  assignedContacts={requestData?.assigned_contacts}
                  required={true}
                  maxHeight="15rem"
                />
              </Stack>
            </Stack>
            {isTaxDeliveryRequest && signableItem && (
              <Stack gap={1} data-tour-id="tax-delivery-signers">
                <InputLabel>CLIENT SIGNATURES</InputLabel>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2">
                    {signableItem.prompt}
                  </Typography>
                  {signableObject?.document_to_sign?.retrieval_token && (
                    <WorkflowSignablePreviewDocument
                      retrievalToken={
                        signableObject.document_to_sign.retrieval_token
                      }
                    />
                  )}
                </Stack>
                <WorkflowSignableDocument
                  signers={signableObject?.signatories}
                />
              </Stack>
            )}
            {isTaxDeliveryRequest && requestData?.reminder_type && (
              <Stack data-tour-id="tax-delivery-reminders">
                <PaymentReminderSettings
                  requestId={requestId}
                  currentReminderType={requestData.reminder_type}
                />
              </Stack>
            )}
            {isTaxDeliveryRequest && <AdvancedOptions />}
          </Stack>
          <Stack
            gap={1}
            padding={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
            })}
          >
            {isTaxDeliveryRequest && (
              <Button
                data-tour-id="tax-delivery-preview-button"
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => setPreviewOpen(true)}
                startIcon={<Icon icon="show" />}
              >
                Preview what your client will see
              </Button>
            )}
            <Tooltip title={parsedValidationErrors} placement="top">
              <span style={{ width: '100%' }}>
                <Button
                  disabled={!isValid || hasValidationErrorsInStore}
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </form>
      {signedUrlData && pdfToken && (
        <SignedUrlPreview
          onClose={() => {
            setPdfToken(null);
            clearSignedUrl();
          }}
          retrievalToken={pdfToken}
          signedUrl={signedUrlData.signed_url}
          fileName={signedUrlData.filename ?? 'File'}
        />
      )}
      {isTaxDeliveryRequest && isPreviewOpen && (
        <ClientPreviewDialog
          open={isPreviewOpen}
          onClose={handleClientPreviewClose}
          openCustomStepDialogSignal={openCustomStepDialogSignal}
          onGuidedTourClose={onGuidedTourClose}
          onClientPreviewTourCandidatesChange={
            onClientPreviewTourCandidatesChange
          }
          clientPreviewTourNavigateSignal={clientPreviewTourNavigateSignal}
          onGuidedCustomStepSaved={onGuidedCustomStepSaved}
          guidedCustomStepCommandSignal={guidedCustomStepCommandSignal}
          onGuidedDemoCommandComplete={onGuidedDemoCommandComplete}
        />
      )}
    </>
  );
}
